import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Dispatch, KeyboardEvent, RefObject, SetStateAction, useRef, useEffect } from "react";
import { InputField, SelectInputField, ComboboxInputField } from "@/components/shared";
import { Organization, Role } from "@/graphql/__generated__/graphql";
import { handleTabKey, toNormalCase } from "@/utils/helpers";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { SelectItem } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CircleLoader } from "@/components/shared/loaders";
import useUserStore from "@/store/shield";
import { createUserDefaultValues, FormSchema } from "../UsersComponent";
import { prefillCreateUserFormWithMockData } from "../mocks";

interface Props {
  open: boolean;
  form: any;
  onSubmit: (_data: z.infer<typeof FormSchema>) => void;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  organizationsData: Organization[];
  organizationsLoading: boolean;
  isProcessing: boolean;
};

const CreateUserDialog = (props: Props) => {
  // Props
  const { open, form, onOpenChange, onSubmit, organizationsLoading, organizationsData, isProcessing } = props;
  const { t } = useTranslation(["modals", "common"]);

  // Check if in development mode
  const isDevMode = import.meta.env.VITE_APP_ENV === "dev";

  // Mock data function for testing
  const handlePrefillMockData = () => {
    const currentOrgId = useUserStore.getState().orgId;
    prefillCreateUserFormWithMockData(form, organizationsData, currentOrgId);
  };

  const inputFieldsData = [
    {
      name: "firstname",
      label: t("createuser.inputs.firstname.label"),
      placeholder: t("createuser.inputs.firstname.placeholder"),
      autoFocus: true,
      type: "text"
    },
    {
      name: "lastname",
      label: t("createuser.inputs.lastname.label"),
      placeholder: t("createuser.inputs.lastname.placeholder"),
      type: "text"
    },
    {
      name: "email",
      label: t("createuser.inputs.email.label"),
      placeholder: t("createuser.inputs.email.placeholder"),
      type: "email",
      className: "flex-1 w-full"
    },
  ];

  // Input field refs
  const inputRefs = Array.from({ length: inputFieldsData.length }, () => useRef<HTMLInputElement>(null));

  // Auto-assign organization if there's only one option
  useEffect(() => {
    if (organizationsData.length === 1) {
      const currentValue = form.getValues("organizationId");
      if (!currentValue) {
        form.setValue("organizationId", String(organizationsData[0].id), { shouldValidate: true });
      }
    }
  }, [organizationsData, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] lg:max-w-fit overflow-y-auto max-h-[90vh] h-full md:h-auto">
        <DialogHeader>
          <DialogTitle>{t("createuser.title")}</DialogTitle>
          <DialogDescription>
            {t("createuser.description")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form} onSubmit={onSubmit} defaultValues={createUserDefaultValues}>
          <form className="space-y-4" id="create-user-form">
            <div className="flex-wrap-4">
              {inputFieldsData.map((fieldData, index) => (
                <InputField
                  key={fieldData.name}
                  name={fieldData.name}
                  label={fieldData.label}
                  autoFocus={fieldData.autoFocus}
                  form={form}
                  className={fieldData.className}
                  type={fieldData.type}
                  ref={inputRefs[index]}
                  placeholder={fieldData.placeholder}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleTabKey(e, inputRefs as RefObject<HTMLInputElement>[])}
                />
              ))}

            </div>
            <div className="flex-wrap-4" data-cy="create-user-select-container">
              <SelectInputField
                name="role"
                triggerClassName="w-full"
                label={t("createuser.inputs.role.label")}
                form={form}
                data-cy="role-select"
                ref={inputRefs[inputFieldsData.length]}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleTabKey(e, inputRefs as RefObject<HTMLInputElement>[])}
              >
                {[Role.Viewer, Role.Editor].map((role) => (
                  <SelectItem key={role} value={role}>
                    {toNormalCase(role)}
                  </SelectItem>
                ))}
              </SelectInputField>

              <ComboboxInputField
                name="organizationId"
                label={t("createuser.inputs.organization.label")}
                form={form}
                data-cy="organization-select"
                ref={inputRefs[inputFieldsData.length + 1]}
                options={organizationsData.map((org) => ({ label: org.name, value: String(org.id) }))}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleTabKey(e, inputRefs as RefObject<HTMLInputElement>[])}
                loading={organizationsLoading}
                popoverProps={{
                  onWheel: (e) => {
                    e.stopPropagation();
                  }
                }}
              />
            </div>
          </form>
        </Form>
        <DialogFooter className="">
          {isDevMode && (
            <Button
              type="button"
              variant="outline"
              onClick={handlePrefillMockData}
              disabled={isProcessing}
              className="mr-auto"
            >
              Prefill Mock Data
            </Button>
          )}
          <DialogClose asChild>
            <Button variant={"outline"} data-cy="create-user-close-button" disabled={isProcessing}>{t("common:buttons.close")}</Button>
          </DialogClose>
          <Button data-cy="create-user-submit" onClick={form.handleSubmit(onSubmit)} type="submit" disabled={isProcessing} className="gap-2">
            <CircleLoader visible={isProcessing} height={16} width={16} strokeWidth={2} />
            {t("common:buttons.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserDialog;
